<template>
  <main>
    <div class="grid container md:gap-4">
      <vf-page-loader v-if="pending" />
      <div class="md:px-10">
        <vf-breadcrumbs
          class="mt-6 gap-x-2"
          :items="[{ label: $t.giftCards, url: '/gift-card-page' }]"
          :last-step="{ label: $t.giftCardBalances }"
        />
      </div>
      <!-- espot: above-balance-check -->
      <cms-section v-if="eSpots?.['above-balance-check']" class="mt-8" :section="eSpots?.['above-balance-check']" />
      <base-grid
        v-if="$feature.allowCheckGiftCardBalance"
        class="my-8 divide-grey-50 <md:space-y-8 md:divide-x"
        :cols="{ md: 2 }"
      >
        <div class="md:px-10 md:pb-24 md:pt-8">
          <vf-notification
            v-if="notification.message"
            class="mb-4"
            :type="notification.type"
            @close="closeNotification"
          >
            {{ notification.message }}
          </vf-notification>
          <h2 class="subtitle-2 mb-4 !title-1">
            {{ $t.giftCardCheckBalance }}
          </h2>
          <p class="text-xs c-grey-10">
            {{ $t.requiredField }}
          </p>
          <base-form ref="formRef" class="mt-2 space-y-4" :form validate-on="submit" @submit="onCheckBalance">
            <vf-form-field
              v-slot="{ attrs }"
              name="giftCard"
              :rule="[
                validateRequired($t.giftCardNumberField),
                validateRegex(giftCardNumberPattern, $t.giftCardFormValidation.cardNumber),
              ]"
            >
              <vf-input v-model="form.giftCard" v-bind="attrs">
                {{ $t.giftCardNumberField }}
              </vf-input>
            </vf-form-field>

            <vf-form-field
              v-if="showGiftCardPin(form.giftCard)"
              v-slot="{ attrs }"
              name="pin"
              :rule="[
                validateRequired($t.giftCardPin),
                validateRegex(cardFormPatterns.cardPin, $t.giftCardFormValidation.cardPin)]"
            >
              <vf-input v-model="form.pin" v-bind="attrs" required>
                {{ $t.giftCardPin }}
                <template #end>
                  <vf-toggletip placement="top-start">
                    <template #trigger>
                      <span class="sr-only">{{ $t.giftCardPin }}</span>
                      <vf-icon name="info" size="md" />
                    </template>
                    {{ $t.giftCardPinTooltip }}
                  </vf-toggletip>
                </template>
              </vf-input>
            </vf-form-field>
            <vf-button class="mt-6 <md:w-full <lg:w-full " type="submit">
              {{ $t.giftCardCheckBalance }}
            </vf-button>
          </base-form>
        </div>
        <div id="balances-container" :aria-busy="pending" aria-live="polite" class="md:px-10 md:pb-24 md:pt-8">
          <h2 id="balances-title" class="subtitle-2 mb-4 !title-1">
            {{ $t.giftCardBalances }}
          </h2>
          <client-only>
            <table aria-labelledby="balances-title" class="mb-8 text-left w-full">
              <thead>
                <tr class="b-b b-grey-50">
                  <th class="text-xs pb-2 b-none fw-medium " scope="col">
                    {{ $t.balance }}
                  </th>
                  <th class="text-xs pb-2 b-none fw-medium text-right " scope="col">
                    {{ $t.giftCardNumber }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="[giftCard, balance] in Array.from(giftCards.entries()).reverse()"
                  :key="giftCard"
                  class="text-xs b-b b-grey-50"
                >
                  <td class="py-4 b-none">
                    <span>{{ useFormattedPrice(balance, currency[useLocale()]) }}</span>
                  </td>
                  <td class="b-none text-right ">
                    {{ maskGiftCard(giftCard, '.') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </client-only>
          <vf-button v-show="!auth.loggedIn" class="<md:w-full <lg:w-full " :to="useLocalisedRoute('/sign-in')" type="submit">
            {{ $t.signInToAccount }}
          </vf-button>
        </div>
      </base-grid>
      <!-- espot: below-balance-check -->
      <cms-section v-if="eSpots?.['below-balance-check']" class="mb-8" :section="eSpots?.['below-balance-check']" />
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'

const { giftCardsBalances } = useCartStore()
const { currency } = useRuntimeConfig().public
const auth = useAuthStore()
const recaptcha = useRecaptcha()
const { cardNumberLengths, cardPinLengths } = useAppConfig().checkout.giftCard
const cardFormPatterns = getGiftCardFormValidationPatterns({ cardNumberLengths, cardPinLengths })
const maxGiftCards = 5
const pending = ref(false)
const isNora = useRegion() === 'NORA'
const giftCardNumberPattern = isNora ? cardFormPatterns.cardNumber : cardFormPatterns.alphanumericCardNumber

const { $t } = useNuxtApp()
const { validateRequired } = useLocalizedValidators()

const { getSections } = useCms()
const eSpots = (await getSections.giftCard()).data.value?.content.sectionsMap

const formRef = ref<InstanceType<typeof BaseFormType>>()

const form = reactive({
  giftCard: '',
  pin: ''
})
const notification = reactive({
  message: '',
  type: 'error' as const
})

const giftCards = computed (() => {
  if (auth.loggedIn)
    return giftCardsBalances

  return ref(new Map<string, number>()).value
})

const onCheckBalance = async () => {
  if (giftCards.value.size >= maxGiftCards) {
    notification.message = $t.giftCardsMaxNumber
    return
  }

  notification.message = ''
  pending.value = true

  try {
    const recaptcha_response = await recaptcha.execute('giftCard')
    const response = await useApi().cart.$getGiftCardBalance({
      gc_number: form.giftCard,
      gc_pin: form.pin,
      recaptcha_response
    })
    const { balance } = response.data
    giftCards.value.set(form.giftCard, balance)
    form.giftCard = ''
    form.pin = ''
    formRef.value?.reset()

    setTimeout(() => {
      document.getElementById('balances-container')?.focus()
    }, 200)
  }
  catch (err: any) {
    assertApiError(err)
    notification.message = err.message
  }
  finally {
    pending.value = false
  }
}

const closeNotification = () => {
  notification.message = ''
}
</script>
